import React from 'react';
import { useTheme } from '@mui/material/styles';
import MuiTypography from '@mui/material/Typography';
import Chart from 'react-apexcharts';
import { Box } from '@mui/material';
import { traducciones } from '../../views/pages/operator/ProductionRecordsPage';

const ProductionVsTargetChart = ({ chartData = ([] = []), height = 350, showLabel = true, isDarkMode = false, showToolbar = true }) => {
    const theme = useTheme();

    const GREEN = theme.palette.primary.main;
    const GREEN_DARK = theme.palette.secondary.main;
    const AXIS_COLOR = isDarkMode ? '#FFFFFF' : theme.palette.grey[700];
    if (!chartData || chartData.length === 0) return null;
    const hours = chartData?.map((p) => (p.hour ? p.hour.substring(0, 5) : ''));

    const producedAccumulated = chartData?.map((p) => p.producedAccumulated ?? null);
    const produced = chartData?.map((p) => p.produced ?? null);
    const targetAccumulated = chartData?.map((p) => p.target ?? null);

    const hasValidData =
        produced.some((v) => v !== null && v !== undefined) ||
        producedAccumulated.some((v) => v !== null && v !== undefined) ||
        targetAccumulated.some((v) => v !== null && v !== undefined);

    const fontFamily = `Inter, sans-serif`;

    const options = {
        chart: {
            type: 'line',
            fontFamily: `'Inter', sans-serif`,
            height: height,
            locales: [traducciones],
            defaultLocale: 'es',
            toolbar: {
                show: showToolbar,
                export: {
                    csv: {
                        filename: `Gráfico de Producción vs Meta.`,
                        headerCategory: 'Horas'
                    },
                    svg: { filename: `Gráfico de Producción vs Meta.` },
                    png: { filename: `Gráfico de Producción vs Meta.` }
                }
            }
        },
        noData: {
            text: 'No hay datos disponibles',
            align: 'center',
            verticalAlign: 'middle',
            style: {
                color: AXIS_COLOR,
                fontSize: '16px',
                fontFamily: fontFamily
            }
        },
        plotOptions: {
            bar: {
                columnWidth: '60%',
                borderRadius: 4
            }
        },

        stroke: {
            curve: 'smooth',
            width: [1, 0, 2],
            dashArray: [5, 0, 0]
        },

        fill: {
            opacity: [1, 0.9, 1]
        },

        markers: {
            size: [0, 0, 4],
            strokeWidth: 0,
            enabledOnSeries: [2]
        },

        dataLabels: {
            enabled: true,
            enabledOnSeries: [2], // ✅ labels solo en Producción
            formatter: (val) => {
                if (!val || val === 0) return '';
                return val;
            },
            style: {
                fontSize: '16px',
                fontWeight: 'bold',
                colors: [GREEN_DARK],
                fontFamily: fontFamily
            },
            offsetY: -12,
            background: {
                enabled: true
            }
        },

        xaxis: {
            categories: hours,
            labels: {
                style: {
                    colors: AXIS_COLOR,
                    fontSize: '16px',
                    fontFamily
                }
            },
            axisBorder: {
                show: true,
                color: AXIS_COLOR
            },
            axisTicks: {
                show: true,
                color: AXIS_COLOR
            }
        },

        yaxis: {
            min: 0,
            labels: {
                style: {
                    colors: AXIS_COLOR,
                    fontSize: '16px',
                    fontFamily
                }
            }
        },

        grid: {
            borderColor: '#eeeeee',
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } }
        },

        tooltip: {
            shared: true,
            intersect: false,
            theme: 'dark',
            y: [
                {
                    formatter: (v) => {
                        if (v === null || v === undefined) return 'Sin producción';
                        else {
                            return v;
                        }
                    }
                }
            ]
        },

        legend: {
            position: 'bottom',
            fontSize: '16px',
            labels: {
                colors: theme.palette.text.primary,
                fontFamily
            }
        }
    };

    const chartSeries = hasValidData
        ? [
              {
                  name: 'Meta',
                  type: 'line',
                  data: targetAccumulated,
                  color: '#FF0000'
              },
              {
                  name: 'Producción (acumulada)',
                  type: 'bar',
                  data: producedAccumulated,
                  color: GREEN
              },
              {
                  name: 'Producción (hora)',
                  type: 'line',
                  data: produced,
                  color: GREEN_DARK
              }
          ]
        : [];

    return (
        <Box>
            {showLabel && (
                <MuiTypography variant="h5" sx={{ fontWeight: 400, textAlign: 'center', color: 'gray' }}>
                    Meta vs Producción por hora.
                </MuiTypography>
            )}

            <Chart options={options} series={chartSeries} type="bar" height={height} />
        </Box>
    );
};

export default ProductionVsTargetChart;
